<template>
  <td
    :ref="(el) => tableUtilityStore.cellRefs.set(cell, el)"
    :class="[
      {
        modified: bulkStore.isModified(product, property),
        readonly: !isEditableProperty,
        active: tableUtilityStore.contextMenu?.id === property.defaultHeader || tableUtilityStore.contextMenu?.id === product.sku,

        selected: tableUtilityStore.tableSelection.cellIsSelected({ columnIndex: -1, rowIndex: cell.rowIndex }), // row is selected
        ...(tableUtilityStore.tableSelection.cellIsSelected(cell) && {
          selected: true,
          'topmost-selected': cell.rowIndex === tableUtilityStore.tableSelection.orderedSelectionData?.topmostRow,
          'bottommost-selected': cell.rowIndex === tableUtilityStore.tableSelection.orderedSelectionData?.bottommostRow,
          'leftmost-selected': cell.columnIndex === tableUtilityStore.tableSelection.orderedSelectionData?.leftmostColumn,
          'rightmost-selected': cell.columnIndex === tableUtilityStore.tableSelection.orderedSelectionData?.rightmostColumn,
        }),

        ...(tableUtilityStore.copied &&
          tableUtilityStore.cellIsWithinCopyBoundary(cell) && {
            copied: true,
            cut: tableUtilityStore.clearCellsOnPaste,
            'topmost-copy-boundary': cell.rowIndex === tableUtilityStore.copied.boundaries?.topmostRow,
            'bottommost-copy-boundary': cell.rowIndex === tableUtilityStore.copied.boundaries?.bottommostRow,
            'leftmost-copy-boundary': cell.columnIndex === tableUtilityStore.copied.boundaries?.leftmostColumn,
            'rightmost-copy-boundary': cell.columnIndex === tableUtilityStore.copied.boundaries?.rightmostColumn,
          }),

        'active-cell': tableUtilityStore.tableSelection.isActiveCell(cell),
        'copy-drag-source': tableUtilityStore.copyDragSources?.has(getCellId(cell)),
      },
    ]"
    @mousedown.left="tableUtilityStore.tableSelection.startSelectionDrag(props.cell, undefined, undefined, props.selectionLimit, $event)"
    @mouseenter="
      tableUtilityStore.tableSelection.handleSelectionDrag(cell, selectionLimit),
        tableUtilityStore.tableSelection.selectionDraggingState && emit('selection', { value: cell })
    "
    @mousedown.exact.shift.left="tableUtilityStore.tableSelection.extendSelectionTo(cell, selectionLimit)"
    @contextmenu="tableUtilityStore.handleOpenContextMenu($event, cell, isProtected)"
    @dblclick="
      bulkStore.editMode
        ? isEditableProperty
          ? emit('focus-cell', { value: cell })
          : tableUtilityStore.showNotWritablePopOver({ x: $event.x, y: $event.y })
        : tableUtilityStore.showNotInEditModePopOver({ x: $event.x, y: $event.y }, undefined, true)
    "
  >
    <PropertyComponent
      :ref="(el) => tableUtilityStore.propertyComponentRefs.set(cell, el)"
      :edit-mode="bulkStore.editMode && tableUtilityStore.selectedIsActive && tableUtilityStore.tableSelection.isSoleSelectedCell(cell)"
      :update-key="
        propertyStore.getProductPropertyKey(product, bulkStore.currentState[product.sku], property) + bulkStore.historyUpdateKey + '_' + bulkStore.editMode
      "
      class="data min-w-100"
      :class="[
        {
          'has-terms': property.hasTerms || property.computedOptions,
        },
        property.type,
      ]"
      :property="property"
      :product="product"
      :product-input="productInput"
      discreet-inputs
      :activate-filter-links="bulkStore.activateProductLinks && !bulkStore.editMode"
      :undo-icon="bulkStore.newProductsSkus?.has(product.sku) ? null : 'fa-undo'"
      :show-value-as-html="false"
      @input="bulkStore.setModified(product.sku, property, $event.containsChanges)"
      @set="bulkStore.setModified(product.sku, property, $event.containsChanges), bulkStore.pushHistoryEntry([{ product, property }])"
      @set-multiple="bulkStore.setModifiedMultiple(product.sku, $event), bulkStore.pushHistoryEntry([{ product, property }])"
      @reset="bulkStore.setModified(product.sku, property, $event.containsChanges), bulkStore.pushHistoryEntry([{ product, property }])"
    >
      <template v-if="shouldUseSkuComponent(property) || (isMainNameProperty(property) && mainLanguage)" #static="{ value, isSku }">
        <div
          v-if="shouldUseSkuComponent(property)"
          :class="{ 'position-absolute overflow-hidden': isSku }"
          :title="isSku && !product.hasTemporarySku && product.availableInCountries ? availableCountriesText : undefined"
        >
          <MoreReadableSkuComponent
            :sku="value?.toString()"
            :is-temporary-sku="isSku ? !!(product.hasTemporarySku && !bulkStore.currentState[product?.sku]?.sku) : false"
            :is-link="bulkStore.activateProductLinks && !bulkStore.editMode"
            class="mr-1"
          />
          <!-- Data for flags in SKU field -->
          <template v-if="isSku && value">
            <img v-for="country of product.availableInCountries" :key="country" :src="getCountryFlag(country)" class="tiny-flag data-for" />
          </template>
        </div>

        <div v-if="isMainNameProperty(property) && value && mainLanguage" class="position-absolute overflow-hidden" style="max-width: calc(100% - 4px)">
          {{ value }}
          <img :src="getFlag(undefined, mainLanguage)" class="tiny-flag language" style="margin-top: -5px" />
        </div>
      </template>

      <template #after>
        <template
          v-if="
            bulkStore.editMode &&
            tableUtilityStore.tableSelection.isRightmostSelectedCell(cell) &&
            tableUtilityStore.tableSelection.singleRowIsSelected &&
            isEditableProperty &&
            !isProtected
          "
        >
          <div class="copy-drag-anchor" @mousedown.left.prevent="tableUtilityStore.startCopyDrag(selectionLimit)" />
        </template>
      </template>
    </PropertyComponent>
  </td>
</template>

<script setup lang="ts">
import MoreReadableSkuComponent from '@/components/simple/MoreReadableSkuComponent.vue'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { Cell } from '@/models/GenericInterfaces'
import { SimplifiedProduct } from '@/models/Product'
import { EditableAdvancedProperty } from '@/models/Property'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsTableUtilityStore } from '@/store/productsTableUtilityStore'
import { usePropertyStore } from '@/store/propertyStore'
import { getCellId } from '@/utils/bulkUtils'
import { isMainNameProperty, productPropertyIsEditable, shouldUseSkuComponent } from '@/utils/propertyUtils'
import { getCountryFlag, getCurrentValue } from '@/utils/tools'
import { computed } from 'vue'
import PropertyComponent from '../../PropertyComponent.vue'
import { useDefaultStore } from '@/store'

const props = defineProps<{
  cell: Cell
  property: EditableAdvancedProperty
  product: SimplifiedProduct
  selectionLimit?: string
  isProtected?: boolean
}>()

const emit = defineEmits(['focus-cell', 'selection'])

const store = useDefaultStore()
const tableUtilityStore = useProductsTableUtilityStore()
const bulkStore = useProductsBulkStore()
const propertyStore = usePropertyStore()
const { translate } = useTranslation()
const { getFlag } = useTools()

const isEditableProperty = computed(() => productPropertyIsEditable(props.property, props.product, productInput.value))

const availableCountriesText = computed(() =>
  props.product.availableInCountries
    ? `${bulkStore.currentState[props.product.sku]?.sku || props.product.sku}\n${props.product.availableInCountries.map((country) => translate(country, 'countries', 'capitalize'))?.join('\n')}`
    : undefined,
)

const productInput = computed(() => bulkStore.currentState[props.product.sku])

const mainLanguage = computed(() => getCurrentValue(productInput.value?.mainLanguage, props.product?.mainLanguage))
</script>

<style lang="scss" scoped></style>
